import api from './api';

function unwrapPayload(data) {
  if (!data || typeof data !== 'object') return data;
  if (data.data !== undefined && data.data !== null && typeof data.data === 'object') {
    return data.data;
  }
  return data;
}

/**
 * Normalize a single notification item from the API.
 */
export function normalizeNotification(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw.data && typeof raw.data === 'object' ? raw.data : {};
  const isUnread =
    raw.is_unread === true
    || raw.isUnread === true
    || (raw.read_at == null && raw.readAt == null);

  return {
    id: raw.id,
    type: raw.type ?? null,
    title: raw.title ?? '',
    body: raw.body ?? '',
    data: {
      envelopeId: data.envelope_id ?? data.envelopeId ?? null,
      publicEnvelopeId: data.public_envelope_id ?? data.publicEnvelopeId ?? null,
      organizationId: data.organization_id ?? data.organizationId ?? null,
      subject: data.subject ?? null,
      envelopeRecipientId:
        data.envelope_recipient_id
        ?? data.envelopeRecipientId
        ?? data.recipient_id
        ?? null,
      link: data.link ?? null,
      linkType: String(data.link_type ?? data.linkType ?? '').toLowerCase() || null,
    },
    readAt: raw.read_at ?? raw.readAt ?? null,
    isUnread,
    createdAt: raw.created_at ?? raw.createdAt ?? null,
    raw,
  };
}

/**
 * Resolve in-app route for a notification.
 * signing → /sign/... ; envelope → /envelopes/{id}/progress
 * account / org types use data.link when present, else a sensible settings fallback
 */
export function getNotificationTargetPath(notification) {
  if (!notification) return null;
  const data = notification.data || {};
  const linkType = String(data.linkType || '').toLowerCase();
  const type = String(notification.type || '').toLowerCase();

  const pathFromAbsoluteLink = (link) => {
    if (!link) return null;
    const s = String(link).trim();
    if (!s) return null;
    if (s.startsWith('/')) return s;
    try {
      const url = new URL(s, window.location.origin);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  };

  if (linkType === 'signing') {
    const fromLink = pathFromAbsoluteLink(data.link);
    if (fromLink && fromLink.startsWith('/sign')) return fromLink;
    if (data.envelopeRecipientId) return `/sign/${data.envelopeRecipientId}`;
    if (fromLink) return fromLink;
  }

  if (linkType === 'envelope') {
    const id = data.publicEnvelopeId || data.envelopeId;
    if (id) return `/envelopes/${id}/progress`;
    const fromLink = pathFromAbsoluteLink(data.link);
    if (fromLink) return fromLink;
  }

  if (linkType === 'billing') {
    const fromLink = pathFromAbsoluteLink(data.link);
    return fromLink || '/settings/billing';
  }

  if (linkType === 'team') {
    const fromLink = pathFromAbsoluteLink(data.link);
    return fromLink || '/settings/users';
  }

  if (linkType === 'login') {
    const fromLink = pathFromAbsoluteLink(data.link);
    return fromLink || '/login';
  }

  // Prefer explicit link from API for account / org notifications
  const fromLink = pathFromAbsoluteLink(data.link);
  if (fromLink) return fromLink;

  // Fallback by available envelope fields
  if (data.envelopeRecipientId && (linkType === 'signing' || !linkType)) {
    return `/sign/${data.envelopeRecipientId}`;
  }
  const envelopeId = data.publicEnvelopeId || data.envelopeId;
  if (envelopeId) return `/envelopes/${envelopeId}/progress`;

  // Account / org / billing notification fallbacks when API omits link
  switch (type) {
    case 'invitation_accepted':
    case 'invitation_expired':
      // Audience is the inviter (typically org admin)
      return '/settings/users';
    case 'quota_nearly_used':
    case 'quota_exhausted':
      return '/plans';
    case 'credit_pack_purchased':
      return '/plans?tab=credit_packs';
    case 'pay_per_send_succeeded':
    case 'subscription_plan_changed':
    case 'subscription_renewed':
    case 'payment_failed':
    case 'subscription_canceled':
    case 'apple_refund_revoke':
    case 'trial_ending':
      // Audience is org billing admin
      return '/settings/billing';
    case 'welcome':
    case 'password_changed':
    case 'role_changed':
    case 'user_suspended':
    case 'user_reactivated':
    case 'user_removed':
      return '/dashboard';
    default:
      return null;
  }
}

/**
 * GET /api/notifications
 * @param {{ per_page?: number, unread_only?: boolean|1 }} [params]
 */
export const listNotifications = async (params = {}) => {
  const query = {};
  if (params.per_page != null) query.per_page = params.per_page;
  if (params.perPage != null) query.per_page = params.perPage;
  if (params.unread_only === true || params.unread_only === 1 || params.unreadOnly === true) {
    query.unread_only = 1;
  }

  const response = await api.get('/notifications', { params: query });
  const root = response.data;
  const payload = unwrapPayload(root);

  let items = [];
  if (Array.isArray(payload)) {
    items = payload;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
  } else if (Array.isArray(payload?.notifications)) {
    items = payload.notifications;
  } else if (Array.isArray(root?.data)) {
    items = root.data;
  }

  const meta = payload?.meta ?? root?.meta ?? null;
  const links = payload?.links ?? root?.links ?? null;

  return {
    items: items.map(normalizeNotification).filter(Boolean),
    meta,
    links,
    raw: root,
  };
};

/**
 * GET /api/notifications/unread-count → { unread_count }
 */
export const getUnreadNotificationCount = async () => {
  const response = await api.get('/notifications/unread-count');
  const payload = unwrapPayload(response.data);
  const count = Number(
    payload?.unread_count
    ?? payload?.unreadCount
    ?? response.data?.unread_count
    ?? 0
  );
  return Number.isFinite(count) ? Math.max(0, count) : 0;
};

/**
 * POST /api/notifications/{id}/read
 */
export const markNotificationRead = async (id) => {
  const response = await api.post(`/notifications/${id}/read`);
  return unwrapPayload(response.data);
};

/**
 * POST /api/notifications/read-all
 */
export const markAllNotificationsRead = async () => {
  const response = await api.post('/notifications/read-all');
  return unwrapPayload(response.data);
};
